import { Box, Button, Grid, TextField, Typography } from "@mui/material"
import dayjs from "dayjs"
import React, { useEffect, useState } from "react"
import ModalDialogPage from "src/components/modal-dialog"
import TableGray from "src/components/table/table-gray"
import EmissaoService from "src/services/emissao-service"
import { DATE_FORMAT_SHOW } from "src/utils/constants"
import PropTypes from 'prop-types'
import SkeletonLine from "src/components/skeleton-line"
import { ordenaPorDataReferencia } from "src/utils/date-format"
import ParameterTable from "src/components/table/parameter-table"
import { calculateMinWidth, renderCellDate, renderTextColumn } from "src/utils/table-render-functions"
import AlertMessage from "src/components/alert-message"

const tableDefault = [
    {
        cells: ["Razão Social: ", "CNPJ: ", "Grupo Econômico: "]
    }
]

const UltimoEventoCreditoDevedor = ({ admin, cpfCnpj, dadosEmpresa, value }) => {
    const ultimoEventoCreditoDevedorDefault = { data_referencia: "", evento_credito_devedor: "", score_evento: "" }
    const [openMostrarTodos, setOpenMostrarTodos] = useState(false)
    const emissaoService = new EmissaoService()
    const [ultimoEventoCreditoDevedor, setUltimoEventoCreditoDevedor] = useState(ultimoEventoCreditoDevedorDefault)
    const [loadingCreditoDevedor, setLoadingCreditoDevedor] = useState(false)
    const [table, setTable] = useState(tableDefault)
    const [rows, setRows] = useState([])
    const [alertMessageState, setAlertMessageState] = React.useState({
            open: false,
            message: "",
            type: ""
    })

    useEffect(() => {
        if (validateValue()) loadData(cpfCnpj)
    }, [cpfCnpj])

    const validateValue = () => {
        if (cpfCnpj) {
            return true
        }

        return false
    }

    const loadData = () => {
        setLoadingCreditoDevedor(true)
        emissaoService.getUltimoEventoCreditoDevedor(cpfCnpj).then((response) => {
            const { body } = response
            const { data } = body

            if (data !== null) {
                const { DataReferencia, EventoCreditoDevedor, ScoreEvento } = data

                setUltimoEventoCreditoDevedor({
                    data_referencia: dayjs(DataReferencia).format(DATE_FORMAT_SHOW),
                    evento_credito_devedor: EventoCreditoDevedor,
                    score_evento: ScoreEvento,
                })
            } else {
                setUltimoEventoCreditoDevedor(ultimoEventoCreditoDevedorDefault)
            }
        }).catch(() => {
            setUltimoEventoCreditoDevedor(ultimoEventoCreditoDevedorDefault)
        }).finally(() => {
            setLoadingCreditoDevedor(false)
        })
    }

    const buildTable = () => {
        setTable([
            {
                cells: [
                    `Razão Social: ${dadosEmpresa?.devedor || ''}`, 
                    `CNPJ: ${dadosEmpresa?.cpfCnpj || ''}`, 
                    `Grupo Econômico: ${dadosEmpresa?.grupoEconomico || ''}`]
            }
        ])
    }

    const openEventoCreditoDevedor = () => {
        if (!validateValue()) {
            setOpenMostrarTodos(true)
            openAlert("Não há dados para o CETIP consultado.", "warning")
            return
        }

        buildTable()
        setOpenMostrarTodos(true)
        setLoadingCreditoDevedor(true)
        emissaoService.eventoCreditoDevedor("get", `?CPFCNPJ=${cpfCnpj.toUpperCase()}`)
            .then((response) => {
                const { body } = response
                const { results } = body
                if (!results || results.length === 0) {
                    openAlert("Não há dados para o CETIP consultado.", "warning")
                } else {
                    setAlertMessageState({ open: false, message: "", type: "" })
                }
                const rows = ordenaPorDataReferencia(results, "desc").map((obj, index) => ({ ...obj, id: index }))
                setRows(rows)
            })
            .catch(() => {
                openAlert("Erro ao buscar os dados. Tente novamente.", "error")
            })
            .finally(() => {
                setLoadingCreditoDevedor(false)
            })
    }

    const deleteItem = async (row) => {
        const { ID } = row

        setLoadingCreditoDevedor(true)
        emissaoService.eventoCreditoDevedor("delete", ID)
            .then(() => { openEventoCreditoDevedor() })
            .finally(() => {
                setLoadingCreditoDevedor(false)
            })
    }

    const addRow = async () => {
        let lastItem = rows[0]

        if (rows.length === 0) {
            lastItem = {
                "DataReferencia": "2023-06-14",
                "DataValidade": "2024-06-14",
                "CETIP": value,
                "CPFCNPJ": cpfCnpj
            }
        }

        setLoadingCreditoDevedor(true)
        emissaoService.eventoCreditoDevedor("post", lastItem)
            .then(() => {
                openEventoCreditoDevedor()
            })
            .catch(() => { })
            .finally(() => {
                setLoadingCreditoDevedor(false)
            })
    }

    const updateRow = async (newRow) => {
        const { ID } = newRow
        setLoadingCreditoDevedor(true)
        emissaoService.eventoCreditoDevedor("patch", ID, newRow)
            .finally(() => {
                setLoadingCreditoDevedor(false)
            })

        return newRow
    }

    const openAlert = (message, type) => {
        setAlertMessageState({
            ...alertMessageState,
            open: true,
            message: message,
            type: type
        })
    }

    const closeAlert = () => {
        setAlertMessageState({...alertMessageState, open: false})
    }

    const minWidthEvento = calculateMinWidth(rows, "EventoCreditoDevedor") / 2

    return <>
        {loadingCreditoDevedor ? (
            <Box sx={{ minHeight: '96px' }}>
                <SkeletonLine />
            </Box>
        ) : (
            <Grid container direction="row" alignItems={"center"} justifyContent="space-between" spacing={2}>
                {ultimoEventoCreditoDevedor.data_referencia || ultimoEventoCreditoDevedor.evento_credito_devedor || ultimoEventoCreditoDevedor.score_evento ? (
                    <>
                        <Grid item xs={12} md={3} sm={12} xl={2}>
                            <TextField fullWidth multiline rows={2} label="Data Referência" value={ultimoEventoCreditoDevedor.data_referencia} />
                        </Grid>
                        <Grid item xs={12} md={3} sm={12} xl={5}>
                            <TextField multiline rows={2} fullWidth label="Evento Crédito" value={ultimoEventoCreditoDevedor.evento_credito_devedor} />
                        </Grid>
                        <Grid item xs={12} md={3} sm={12} xl={2}>
                            <TextField fullWidth multiline rows={2} label="Score Evento" value={ultimoEventoCreditoDevedor.score_evento} />
                        </Grid>
                    </>
                ) : (
                    <Grid item xs={8}>
                        <Typography variant="body2" color="black" align="center">
                            Não há evento de crédito devedor para o CETIP consultado.
                        </Typography>
                    </Grid>
                )}
                <Grid item xs={12} md={3} sm={12} xl={3}>
                    <Button fullWidth variant="contained" onClick={openEventoCreditoDevedor}>Mostrar Todos</Button>
                </Grid>
            </Grid>
        )}

        <ModalDialogPage open={openMostrarTodos}
            title="Histórico Evento de Crédito Devedor"
            close={() => {
                setOpenMostrarTodos(false)
            }}>
            <>
                <Grid container direction="column" spacing={2}>
                    <Grid item xs={12} md={12} xl={12}>
                        <TableGray data={table} />
                            <Grid item xs={12} style={{ marginTop: 8 }}>
                                <AlertMessage open={alertMessageState.open} message={alertMessageState.message} type={alertMessageState.type} close={closeAlert}/>
                            </Grid>                       
                    </Grid>
                </Grid>
                <br />
                <Grid container direction="column">
                    <Grid item xs={12} md={12} xl={12}>
                        <ParameterTable
                            columns={[
                                { field: 'DataReferencia', headerName: 'Data Referencia', flex: 1, editable: admin, renderCell: renderCellDate },
                                { field: 'EventoCreditoDevedor', headerName: 'Evento', flex: 1, minWidth: minWidthEvento, editable: admin, renderCell: renderTextColumn },
                                { field: 'ScoreEvento', headerName: 'Score Evento', flex: 1, editable: admin, },
                            ]}
                            rowHeigth="auto"
                            data={rows}
                            setData={setRows}
                            createRow={addRow}
                            deleteRow={deleteItem}
                            updateRow={updateRow}
                            loadData={loadData}
                            disableDeleteColumn={!admin}
                            disableAddRow={!admin}
                            buttonAddVariant={"contained"}
                            buttonAddTitle={"Adicionar Rating"}
                        />
                    </Grid>
                </Grid>
            </>
        </ModalDialogPage>
    </>
}

UltimoEventoCreditoDevedor.propTypes = {
    admin: PropTypes.bool,
    cpfCnpj: PropTypes.string,
    dadosEmpresa: PropTypes.object,
    loading: PropTypes.func,
    value: PropTypes.string,
}


export default UltimoEventoCreditoDevedor